import { Paper, Text, Group, createStyles, Box, LoadingOverlay, Divider, useMantineTheme } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import useDasboardApi from '../../hooks/useDashboardApi';
import DetailCard from './card-components/DetailCard';
import TimeSelector from './card-components/TimeSelector';
import FilterSelector from './selectors/FilterSelector';

// Custom styles
const useStyles = createStyles((theme) => ({
	container: {
		padding: '20px 0 10px'
	},
	text: {
		fontSize: 36,
		lineHeight: '42px',
		fontWeight: 400,
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	},
	cardTitle: {
		fontWeight: 600,
		fontSize: 16,
		lineHeight: '20px',
		color: theme.colorScheme === 'light' ? '#4A4A4A' : '#C1C2C5',
	}
}));

export default function ActiveUsers() {

	// State and hooks
	const { classes } = useStyles();
	const [selectedRange, setSelectedRange] = useState('total');
	const [range, setRange] = useState([null, null]);
	const [visible, setVisible] = useState(false);
	const { getActiveUsersStats, getActiveUsersHeatmap } = useDasboardApi();
	const { activeUsersStats, activeUsersHeatmap, userTypes } = useSelector(state => state.dashboardData);
	const theme = useMantineTheme();
	const [filters, setFilters] = useState([]);
	const [typesSeries, setTypesSeries] = useState([]);
	const [ageSeries, setAgeSeries] = useState([]);
	const foreColor = theme.colorScheme === 'light' ? theme.colors.dark[8] : theme.colors.gray[0];
	const [typesOptions, setTypesOptions] = useState({
		chart: {
			type: 'donut',
			toolbar: {
				show: false
			},
			zoom: {
				enabled: false
			},
			foreColor: foreColor,
		},
		fill: {
			colors: theme.other.chartOrange,
		},
		colors: theme.other.chartOrange,
		plotOptions: {
			pie: {
				donut: {
					size: '55%'
				}
			}
		}
	});
	const [ageOptions, setAgeOptions] = useState({
		chart: {
			type: 'donut',
			toolbar: {
				show: false
			},
			zoom: {
				enabled: false
			},
			foreColor: foreColor,
		},
		fill: {
			colors: theme.other.chartPurple,
		},
		colors: theme.other.chartPurple,
		plotOptions: {
			pie: {
				donut: {
					size: '55%'
				}
			}
		}
	});
	const [activitySeries, setActivitySeries] = useState([]);
	const activityOptions = {
		chart: {
			type: 'heatmap',
			toolbar: {
				show: false
			},
			zoom: {
				enabled: false
			},
			foreColor: foreColor,
		},
		states: {
			hover: {
				filter: {
					type: 'none'
				}
			},
			active: {
				filter: {
					type: 'none',
				}
			}
		},
		plotOptions: {
			heatmap: {
				states: {},
				shadeIntensity: 0.3,
				radius: 4,
				distributed: true,
				colorScale: {
					ranges: [{
						from: 0,
						to: 1000,
						name: '0 a 1k',
						color: theme.other.chartPurple[0]
					}, {
						from: 1001,
						to: 1999,
						name: '1k a 1.9k',
						color: theme.other.chartPurple[1]
					}, {
						from: 2000,
						to: 4999,
						name: '2k a 4.9k',
						color: theme.other.chartPurple[2]
					}, {
						from: 5000,
						to: 999999,
						name: '+5k',
						color: theme.other.chartPurple[3]
					}]
				}
			}
		},
		dataLabels: {
			enabled: false
		},
		stroke: {
			width: 6
		},
		xaxis: {
			type: 'category',
			tooltip: {
				enabled: false
			},
		},
		legend: {
			position: 'bottom',
			horizontalAlign: 'center',
			itemMargin: {
				vertical: 10
			}
		},
	};
	const [levelSeries, setLevelSeries] = useState([]);
	const [levelOptions, setLevelOptions] = useState({
		chart: {
			type: 'bar',
			toolbar: {
				show: false
			},
			zoom: {
				enabled: false
			},
			foreColor: foreColor,
		},
		states: {
			hover: {
				filter: {
					type: 'none'
				}
			}
		},
		fill: {
			colors: theme.other.chartPurple,
		},
		colors: theme.other.chartPurple,
		plotOptions: {
			bar: {
				borderRadius: 4,
				horizontal: true,
			}
		},
		dataLabels: {
			enabled: true,
			formatter: function (val, opts) {
				const sum = opts.w.config.series[opts.seriesIndex].data.reduce((acc, cur) => acc + cur, 0);
				const percentage = (val / sum * 100).toFixed(0);
				return isNaN(percentage) ? '0%' : `${percentage}%`;
			},
			offsetX: 0,
			style: {
				fontSize: '12px',
				colors: ['#000000']
			}
		},
	});

	// On load
	useEffect(() => {
		async function fetchData() {
			await getActiveUsersStats('', '', filters);
			await getActiveUsersHeatmap('', '', filters);
		}
		fetchData();
		// eslint-disable-next-line
	}, []);

	// Listen to selected range changes
	useEffect(() => {

		// Only if initial data has loaded
		if (activeUsersStats !== null && activeUsersHeatmap !== null) reload();

		// eslint-disable-next-line
	}, [selectedRange, filters]);

	// On data load
	useEffect(() => {

		// If not null
		if (activeUsersStats) {

			// Get labels
			const typesLabels = activeUsersStats.typeCount.map(item => item.name);
			const ageSeries = Object.values(activeUsersStats.ageGroups);
			const ageLabels = Object.keys(activeUsersStats.ageGroups);
			
			// Convert labelst to ranges
			const ageRanges = ageLabels.map(category => {
				switch (category) {
					case 'young': return '18-24';
					case 'middle': return '25-30';
					case 'adult': return '31-40';
					case 'senior': return '+40';
					default: return category;
				}
			});

			// Assign chart values
			setTypesSeries(activeUsersStats.typeCount.map(item => item.total));
			setTypesOptions(prevState => ({
				...prevState,
				labels: typesLabels,
				chart: {
					foreColor: foreColor
				}
			}));
			setAgeSeries(ageSeries);
			setAgeOptions(prevState => ({
				...prevState,
				labels: ageRanges,
				chart: {
					foreColor: foreColor
				}
			}));
			setLevelSeries([{
				name: 'Total',
				data: activeUsersStats.levelCount.map(item => item.total),
			}]);
			setLevelOptions(prevState => ({
				...prevState,
				xaxis: {
					categories: activeUsersStats.levelCount.map(item => item.name),
				},
				chart: {
					foreColor: foreColor
				},
				tooltip: {
					enabled: true,
					followCursor: true,
					custom: function ({ series, seriesIndex, dataPointIndex, w }) {

						// Extracting the total value for the level
						const total = series[seriesIndex][dataPointIndex];
						// Extracting the level name using the category (x-axis) at the dataPointIndex
						const levelName = w.globals.labels[dataPointIndex];

						// Return custom HTML content for the tooltip
						return `<div style="text-align: center; padding: 5px; font-size: 12px;">${levelName}: <strong>${total}</strong></div>`;

					}
				}
			}));

		}

		// eslint-disable-next-line
	}, [activeUsersStats]);

	// Convert number to day of the week
	const numberToDay = (num) => {

		// Build array
		const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

		// Return value
		return days[num - 1];

	};

	// On data load
	useEffect(() => {

		// If not null
		if (activeUsersHeatmap) {

			// Variables
			const timeRangeMap = {};

			// Loop information
			activeUsersHeatmap.forEach(item => {

				// Conditions
				if (!timeRangeMap[item.timeRange]) {
					timeRangeMap[item.timeRange] = {
						name: item.timeRange,
						data: []
					};
				}

				// Add information to series
				timeRangeMap[item.timeRange].data.push({
					x: numberToDay(item.weekDay),
					y: item.activitiesCount
				});

			});

			// Assign to state
			const processedSeries = Object.values(timeRangeMap);
			setActivitySeries(processedSeries);

		}

	}, [activeUsersHeatmap]);

	// Reload with new values
	const reload = async () => {

		// Set loader
		setVisible(true);

		// Define date variables
		let endDate = new Date();
		let startDate = new Date();

		// Check date range to apply
		switch (selectedRange) {
			case '30d':
				startDate.setDate(endDate.getDate() - 30);
				break;
			case '7d':
				startDate.setDate(endDate.getDate() - 7);
				break;
			case 'total':
				// Remove both dates
				endDate = '';
				startDate = '';
				break;
			case 'custom':
				// Assign custom range
				endDate = range[1];
				startDate = range[0];
				break;
			default:
				// Remove both dates
				endDate = '';
				startDate = '';
				break;
		}

		// Get information
		if (selectedRange) await getActiveUsersStats(startDate, endDate, filters);
		if (selectedRange) await getActiveUsersHeatmap(startDate, endDate, filters);

		// Remove loader
		setVisible(false);

	}

	return (
		<Paper
			withBorder
			p={12}
			style={{ position: 'relative' }}
			mt={30}
			radius={18}
		>
			{/* Loading Overlay */}
			<LoadingOverlay
				visible={visible}
				overlayBlur={1}
				radius={18}
			/>
			{/* Top */}
			<Group
				position="apart"
				noWrap
			>
				{/* Title */}
				<Text className={classes.cardTitle}>
					Activos
				</Text>
			</Group>
			{/* Time & Filter */}
			<Group
				position="apart"
				mt={8}
				noWrap
				align="center"
			>
				{/* Range */}
				<TimeSelector
					data={activeUsersStats}
					selectedRange={selectedRange}
					setSelectedRange={setSelectedRange}
					range={range}
					setRange={setRange}
				/>
				{/* Filter */}
				<FilterSelector
					label="Usuarios"
					options={userTypes}
					selected={[filters, setFilters]}
				/>
			</Group>
			{/* Content */}
			<Box className={classes.container}>
				{/* Number */}
				<Text className={classes.text}>
					{activeUsersStats ? new Intl.NumberFormat('es-AR', { notation: 'compact' }).format(activeUsersStats.totalUsers).replace(/\s(?=K|k|M|B|T)/, '') : 0}
				</Text>
				<Divider
					mt={10}
					mb={30}
					color="#EEEEEE"
				/>
				{/* Subtitle */}
				<Text
					className={classes.cardTitle}
					mb={10}
				>
					Promedios por usuario
				</Text>
				{/* Container */}
				<Group grow>
					{/* Savings */}
					<DetailCard
						title={<>Ahorro CO<sub>2</sub></>}
						text={activeUsersStats ? new Intl.NumberFormat('es-AR', { style: 'unit', unit: 'kilogram', notation: 'compact' }).format(activeUsersStats?.avgCo2).replace(/\s(?=K|k|M|B|T)/, '') : '0,0 kg'}
						style={{
							position: 'relative',
							borderColor: '#95C466'
						}}
					/>
					{/* Distance */}
					<DetailCard
						title="Distancia Recorrida"
						text={activeUsersStats ? new Intl.NumberFormat('es-AR', { style: 'unit', unit: 'kilometer', notation: 'compact' }).format(activeUsersStats?.avgDistance).replace(/\s(?=K|k|M|B|T)/, '') : '0,0 km'}
						style={{ position: 'relative' }}
					/>
					{/* Time */}
					<DetailCard
						title="Tiempo en Bici"
						text={activeUsersStats ? `${activeUsersStats.avgTime?.years}a ${activeUsersStats.avgTime?.days}d ${activeUsersStats.avgTime?.hours}h` : '0a 0d 0h'}
						style={{ position: 'relative' }}
					/>
				</Group>
				<Divider
					mt={20}
					mb={40}
					color="#EEEEEE"
				/>
				{/* Container */}
				<Group
					grow
					align="flex-start"
					spacing={30}
				>
					{/* Content */}
					<Box>
						{/* Title */}
						<Text
							className={classes.cardTitle}
							pb={10}
						>
							Tipos de usuario
						</Text>
						{/* Types Chart */}
						{typesSeries.every(element => element === 0) ?
							<Text
								pt={20}
								fz={14}
								c="dimmed"
							>
								No hay datos para mostrar
							</Text>
						:
							<ReactApexChart
								options={typesOptions}
								series={typesSeries}
								type="donut"
							/>
						}
					</Box>
					{/* Content */}
					<Box>
						{/* Title */}
						<Text
							className={classes.cardTitle}
							pb={10}
						>
							Edad
						</Text>
						{/* Age Chart */}
						{ageSeries.every(element => element === 0) ?
							<Text
								pt={20}
								fz={14}
								c="dimmed"
							>
								No hay datos para mostrar
							</Text>
						:
							<ReactApexChart
								options={ageOptions}
								series={ageSeries}
								type="donut"
							/>
						}
					</Box>
				</Group>
				<Divider
					mt={20}
					mb={40}
					color="#EEEEEE"
				/>
				{/* Container */}
				<Group
					grow
					align="flex-start"
					spacing={30}
				>
					{/* Content */}
					<Box>
						{/* Title */}
						<Text
							className={classes.cardTitle}
							pb={10}
						>
							Usuarios por nivel
						</Text>
						{/* Types Chart */}
						<ReactApexChart
							options={levelOptions}
							series={levelSeries}
							type="bar"
							height={300}
						/>
					</Box>
					{/* Content */}
					<Box>
						{/* Title */}
						<Text
							className={classes.cardTitle}
							pb={10}
						>
							Actividad
						</Text>
						{/* Activity Chart */}
						<ReactApexChart
							options={activityOptions}
							series={activitySeries}
							type="heatmap"
							height={300}
						/>
					</Box>
				</Group>
			</Box>
		</Paper>
	);

}
